import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { works, categoryList } from "@/mocks/works";

const statusMap: Record<string, string> = {
  连载中: "bg-accent-500",
  已完结: "bg-primary-500",
  休载: "bg-foreground-500",
};

export default function Works() {
  useSeo(
    "漫画作品库 - 178漫画网，按作品和题材找到同好讨论",
    "178漫画网漫画作品库，覆盖热血、悬疑、恋爱、科幻、奇幻、校园、治愈等多种题材。每部漫画下方都有讨论区，随时找到同好交流剧情、分享心得。",
  );

  const [searchParams] = useSearchParams();
  const [active, setActive] = useState("全部");
  const [keyword, setKeyword] = useState(searchParams.get("q") ?? "");

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    return works.filter((w) => {
      const matchCat = active === "全部" || w.category === active;
      const matchKw =
        !kw ||
        w.title.includes(kw) ||
        w.author.includes(kw) ||
        w.tags.some((t) => t.includes(kw)) ||
        w.synopsis.includes(kw);
      return matchCat && matchKw;
    });
  }, [active, keyword]);

  const searchChange = (v: string) => {
    setKeyword(v);
    const url = new URL(window.location.href);
    if (v.trim()) url.searchParams.set("q", v.trim());
    else url.searchParams.delete("q");
    window.history.replaceState(null, "", url.toString());
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16 md:pt-20">
        {/* 页头 */}
        <section className="bg-background-100 border-b border-background-200">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-700">漫画作品</span>
            </nav>
            <h1 className="mt-3 font-heading text-2xl md:text-4xl text-foreground-950">
              漫画作品库
            </h1>
            <p className="mt-2 max-w-2xl text-sm md:text-base text-foreground-600">
              按题材浏览178漫画网收录的漫画作品，每部漫画下方都有讨论区，热门作品更有专属圈子，找到同好开聊。
            </p>
            <div className="mt-6 flex max-w-md items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-1.5">
              <i className="ri-search-line text-foreground-400" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => searchChange(e.target.value)}
                placeholder="搜索作品名、作者或标签"
                className="w-full bg-transparent py-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {keyword && (
                <button type="button" onClick={() => searchChange("")} aria-label="清除搜索">
                  <i className="ri-close-circle-fill text-foreground-300 hover:text-primary-500" />
                </button>
              )}
            </div>
          </div>
        </section>

        {/* 分类筛选 */}
        <section className="bg-background-50 py-8 md:py-12">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="flex flex-wrap items-center gap-2">
              {categoryList.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setActive(c)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    active === c
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="mt-6 flex items-center justify-between">
              <p className="text-sm text-foreground-600">
                共 <strong className="text-primary-600">{filtered.length}</strong> 部作品
              </p>
              <span className="text-xs text-foreground-500">按热度排序</span>
            </div>

            {filtered.length > 0 ? (
              <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {filtered.map((w) => (
                  <article key={w.id} className="group overflow-hidden rounded-2xl border border-background-200 bg-background-100 hover:border-primary-300 transition-all">
                    <Link to={`/post/${w.id}`} className="block">
                      <div className="relative aspect-[3/4] overflow-hidden">
                        <img
                          src={w.cover}
                          alt={`${w.title}漫画作品封面`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                        <span className={`absolute left-2 top-2 rounded-full ${statusMap[w.status]} text-background-50 px-2.5 py-1 text-xs font-medium`}>
                          {w.status}
                        </span>
                      </div>
                    </Link>
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <h2 className="font-heading text-base text-foreground-950 line-clamp-1">{w.title}</h2>
                        <span className="shrink-0 text-sm font-bold text-secondary-600">★ {w.rating}</span>
                      </div>
                      <p className="mt-1 text-xs text-foreground-500">{w.author} · {w.episodes}话</p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {w.tags.map((t) => (
                          <span key={t} className="rounded-full bg-background-200/70 px-2.5 py-0.5 text-xs text-foreground-600">#{t}</span>
                        ))}
                      </div>
                      <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-600">
                        <span className="inline-flex items-center gap-1"><i className="ri-user-heart-line text-primary-500" />{w.followers} 同好</span>
                        <Link to={`/post/${w.id}`} className="inline-flex items-center gap-0.5 font-medium text-primary-600 hover:text-primary-500">
                          讨论区 <i className="ri-arrow-right-s-line" />
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="mt-10 rounded-2xl border border-dashed border-background-300 bg-background-100 py-16 text-center">
                <i className="ri-inbox-2-line text-4xl text-foreground-300" />
                <p className="mt-3 text-sm text-foreground-500">没有找到匹配的作品，换个关键词试试吧</p>
                <button
                  type="button"
                  onClick={() => { setActive("全部"); searchChange(""); }}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 hover:bg-primary-600 whitespace-nowrap"
                >
                  查看全部作品
                </button>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}